'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { identifyUser, resetUser, initMixpanel } from '@/lib/analytics';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const supabase = createClient();

  useEffect(() => {
    initMixpanel();
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchSubscription(session.user.id);
      }
    });

    const {
      data: { subscription: authSub },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchSubscription(session.user.id);
      } else {
        setProfile(null);
        setSubscription(null);
        resetUser();
      }
    });

    return () => authSub.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data) {
        setProfile(data);
        identifyUser(userId, {
          email: data.email,
          name: data.full_name,
          profession: data.profession,
          plan: data.plan,
        });
      }
    } catch {}
  };

  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (!error && data) setSubscription(data);
    } catch {}
  };

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Update User Profile
  const updateProfile = async (updates: any) => {
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  };

  // Get Team Members
  const getTeamMembers = async () => {
    if (!user) return [];
    const { data, error } = await supabase
      .from('team_members')
      .select(`
        id,
        role,
        invite_status,
        invite_email,
        joined_at,
        user_profiles (id, full_name, email, avatar_url)
      `)
      .eq('teams.owner_id', user.id);
    if (error) return [];
    return data || [];
  };

  // Get User API Keys
  const getApiKeys = async () => {
    if (!user) return [];
    const { data, error } = await supabase
      .from('user_api_keys')
      .select('*')
      .eq('user_id', user.id)
      .order('provider_category');
    if (error) return [];
    return data || [];
  };

  // Save API Key
  const saveApiKey = async (providerId: string, category: string, label: string, key: string) => {
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('user_api_keys')
      .upsert(
        {
          user_id: user.id,
          provider_id: providerId,
          provider_category: category,
          provider_label: label,
          encrypted_key: key,
          is_connected: key.length > 8,
        },
        { onConflict: 'user_id,provider_id' }
      )
      .select()
      .single();
    if (error) throw error;
    return data;
  };

  // Delete API Key
  const deleteApiKey = async (providerId: string) => {
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase
      .from('user_api_keys')
      .delete()
      .eq('user_id', user.id)
      .eq('provider_id', providerId);
    if (error) throw error;
  };

  // Get Subscription
  const getSubscription = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) return null;
    return data;
  };

  // Update Subscription after payment
  const updateSubscription = async (updates: any) => {
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('subscriptions')
      .upsert({ user_id: user.id, ...updates }, { onConflict: 'user_id' })
      .select()
      .single();
    if (error) throw error;
    setSubscription(data);
    return data;
  };

  const value = {
    user,
    session,
    loading,
    profile,
    subscription,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    updateProfile,
    getTeamMembers,
    getApiKeys,
    saveApiKey,
    deleteApiKey,
    getSubscription,
    updateSubscription,
    refreshProfile: () => user && fetchProfile(user.id),
    refreshSubscription: () => user && fetchSubscription(user.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
